import { useEffect, useMemo, useState } from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { getSurvey, submitSurveyResponse } from './surveysApi';
import { evalGate } from './gate';
import TestimonialForm from './TestimonialForm';
import type { Answers, Question, SurveyInstrumentPublic } from './types';

type LoadState =
  | { phase: 'loading' }
  | { phase: 'closed' }
  | { phase: 'error' }
  | { phase: 'ready'; instrument: SurveyInstrumentPublic; completed: boolean };

function isAnswered(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

function ScaleInput({ q, value, onChange }: { q: Question; value: unknown; onChange: (v: number) => void }) {
  const min = q.min ?? 1;
  const max = q.max ?? 5;
  const options = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {options.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`h-9 min-w-9 rounded-lg border px-3 text-sm font-medium transition-colors ${
              value === n
                ? 'border-ws-accent bg-ws-accent text-ws-on-accent'
                : 'border-ws-border bg-ws-surface text-ws-ink-2 hover:border-ws-border-strong'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      {(q.min_label || q.max_label) && (
        <div className="mt-1 flex justify-between text-xs text-ws-ink-4">
          <span>{q.min_label}</span>
          <span>{q.max_label}</span>
        </div>
      )}
    </div>
  );
}

function ChoiceInput({ q, value, onChange }: { q: Question; value: unknown; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      {(q.options ?? []).map((opt) => (
        <label key={opt} className="flex cursor-pointer items-start gap-2 text-sm text-ws-ink-2">
          <input
            type="radio"
            name={q.id}
            className="mt-0.5"
            checked={value === opt}
            onChange={() => onChange(opt)}
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function MultiInput({ q, value, onChange }: { q: Question; value: unknown; onChange: (v: string[]) => void }) {
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const toggle = (opt: string) => {
    onChange(selected.includes(opt) ? selected.filter((o) => o !== opt) : [...selected, opt]);
  };
  return (
    <div className="space-y-1.5">
      {(q.options ?? []).map((opt) => (
        <label key={opt} className="flex cursor-pointer items-start gap-2 text-sm text-ws-ink-2">
          <input type="checkbox" className="mt-0.5" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function TextInput({ q, value, onChange }: { q: Question; value: unknown; onChange: (v: string) => void }) {
  const v = typeof value === 'string' ? value : '';
  const maxLen = q.max_len ?? 4000;
  return (
    <div>
      <textarea
        rows={3}
        maxLength={maxLen}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-ws-border bg-ws-surface p-2.5 text-sm text-ws-ink outline-none focus:border-ws-accent"
      />
      <div className="mt-0.5 text-right text-xs text-ws-ink-4">{v.length}/{maxLen}</div>
    </div>
  );
}

/** Renders every question of one survey instrument, by key. */
export default function SurveyRunner({ instrumentKey }: { instrumentKey: string }) {
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [answers, setAnswers] = useState<Answers>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showTestimonialFor, setShowTestimonialFor] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ phase: 'loading' });
    getSurvey(instrumentKey)
      .then(({ instrument, completed }) => {
        if (cancelled) return;
        setState({ phase: 'ready', instrument, completed });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) setState({ phase: 'closed' });
        else setState({ phase: 'error' });
      });
    return () => { cancelled = true; };
  }, [instrumentKey]);

  const visibleQuestions = useMemo(() => {
    if (state.phase !== 'ready') return [];
    return state.instrument.questions.filter((q) => evalGate(q.gate, answers));
  }, [state, answers]);

  if (state.phase === 'loading') return <Spinner label="Loading survey…" />;
  if (state.phase === 'error') {
    return (
      <Card className="p-6 text-center text-sm text-ws-danger">Could not load this survey. Please try again.</Card>
    );
  }
  if (state.phase === 'closed') {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm font-medium text-ws-ink">This survey isn’t open right now.</p>
        <p className="mt-1 text-xs text-ws-ink-3">Check back during the window your instructor announced.</p>
      </Card>
    );
  }
  if (state.completed || submitted) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm font-medium text-ws-ink">Thanks — your response is recorded.</p>
        <p className="mt-1 text-xs text-ws-ink-3">Your answers aren’t linked to your name.</p>
      </Card>
    );
  }

  const { instrument } = state;

  const setAnswer = (id: string, v: unknown) => {
    setAnswers((a) => ({ ...a, [id]: v }));
    setErrors((e) => { const n = { ...e }; delete n[id]; return n; });
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    for (const q of visibleQuestions) {
      if (q.type === 'info') continue;
      if (q.required && !isAnswered(answers[q.id])) next[q.id] = 'This one is required.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Only send answers to questions currently visible (gated-shut answers
      // are never sent — the server would 400 on them anyway).
      const visibleIds = new Set(visibleQuestions.map((q) => q.id));
      const payload: Answers = {};
      for (const [k, v] of Object.entries(answers)) if (visibleIds.has(k)) payload[k] = v;
      await submitSurveyResponse(instrumentKey, payload);
      setSubmitted(true);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { error?: string } } })?.response?.status;
      if (status === 409) setSubmitted(true); // already completed elsewhere — treat as done
      else if (status === 403) setState({ phase: 'closed' });
      else setSubmitError('Some answers could not be accepted. Please check the form and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-5 sm:p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-ws-ink">{instrument.title}</h2>
        {instrument.description && <p className="mt-1 text-sm text-ws-ink-3">{instrument.description}</p>}
      </header>

      <div className="space-y-5">
        {visibleQuestions.map((q) => {
          if (q.type === 'info') {
            return (
              <div key={q.id} className="rounded-lg border border-ws-accent/30 bg-ws-accent-quiet p-4">
                <p className="text-sm text-ws-ink">{q.prompt}</p>
                {q.cta && (
                  <Button size="sm" className="mt-3" onClick={() => setShowTestimonialFor(q.id)}>
                    {q.cta}
                  </Button>
                )}
              </div>
            );
          }
          return (
            <div key={q.id}>
              <label className="mb-1.5 block text-sm font-medium text-ws-ink">
                {q.prompt}
                {q.required && <span className="ml-1 text-ws-danger">*</span>}
              </label>
              {q.help && <p className="mb-1.5 text-xs text-ws-ink-4">{q.help}</p>}
              {q.type === 'scale' && (
                <ScaleInput q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
              )}
              {q.type === 'choice' && (
                <ChoiceInput q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
              )}
              {q.type === 'multi' && (
                <MultiInput q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
              )}
              {q.type === 'text' && (
                <TextInput q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
              )}
              {errors[q.id] && <p className="mt-1 text-xs text-ws-danger">{errors[q.id]}</p>}
            </div>
          );
        })}
      </div>

      {submitError && <p className="mt-4 text-sm text-ws-danger">{submitError}</p>}

      <div className="mt-6 flex items-center justify-end">
        <Button onClick={handleSubmit} loading={submitting}>Submit</Button>
      </div>

      {showTestimonialFor && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-ws-overlay p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-ws-surface p-5 shadow-ws-shadow-lg">
            <TestimonialForm onDone={() => setShowTestimonialFor(null)} />
          </div>
        </div>
      )}
    </Card>
  );
}
